<template>
  <div class="dropdown-cnt" ref="dropdownRef">
    <div class="dropdown-input-cnt" @click.stop="toggleDropdown">
      <BackstageInputLabel :label="label" :required="required" :class="{ disabled: disabled }">
        <template #tooltip>
          <slot name="tooltip" />
        </template>
        <input
          :name="name"
          v-model="inputValue"
          class="combobox-input"
          :placeholder="placeholder || t('global.select_placeholder')"
          :disabled="disabled"
          @keydown.stop="handleKeydown"
          @blur="handleBlur"
          autocomplete="off" />
      </BackstageInputLabel>
      <BackstageIcon :name="Icons.CHEVRON_DOWN" class="combobox-icon" />
    </div>

    <div v-if="isOpen && !disabled" class="dropdown-list-cnt">
      <ul class="dropdown-list" :data-cy="optionsDataCy">
        <li
          v-for="(option, index) in filteredOptions"
          :id="`option-${index}`"
          :key="option.value"
          @click.stop="selectOption(option)"
          @mouseenter="hoveredIndex = index"
          :aria-selected="index === hoveredIndex"
          :class="['dropdown-item', { active: option.value === props.modelValue, hover: index === hoveredIndex }]">
          {{ option.label }}
        </li>
        <li v-if="filteredOptions.length === 0" class="dropdown-item">
          {{ noOptionsText ?? t('forms.options.noOptionsFound') }}
        </li>
      </ul>
    </div>
    <ErrorMessage v-if="errorMessage && meta.touched" :name="name" />
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { ErrorMessage, useField } from 'vee-validate';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import BackstageInputLabel from '@/components/common/BackstageInputLabel.vue';
import { Icons } from '@/models/icons.enum';
import BackstageIcon from './BackstageIcon.vue';

export interface ComboboxOption {
  value: string;
  label: string;
}

interface Props {
  options: ComboboxOption[];
  optionsDataCy: string;
  name?: string;
  label?: string;
  placeholder?: string;
  modelValue?: string;
  required?: boolean;
  disabled?: boolean;
  noOptionsText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  name: '',
  label: '',
  modelValue: '',
  required: false,
  disabled: false,
});

const emit = defineEmits<{ (e: 'update:modelValue', val: any): void; (e: 'click', val: string): void }>();
const { t } = useI18n();
const dropdownRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const hoveredIndex = ref(-1);

const { value: inputValue, errorMessage, meta, handleBlur } = useField<string>(props.name);

const selectedLabel = computed<string>(() => {
  if (!props.modelValue) return '';
  const option = props.options.find((o) => o.value === props.modelValue);

  return option ? option.label : '';
});

const filteredOptions = computed<ComboboxOption[]>(() => {
  if (props.modelValue || !inputValue.value) {
    return props.options;
  }

  return props.options.filter((option) => option.label.toLowerCase().includes(inputValue.value.toLowerCase()));
});

watch(
  selectedLabel,
  (newLabel) => {
    inputValue.value = newLabel;
  },
  { immediate: true },
);

watch(inputValue, (newValue, oldValue) => {
  if (!props.modelValue && newValue && !isOpen.value) {
    isOpen.value = true;
  }

  if (isOpen.value && newValue !== oldValue) {
    hoveredIndex.value = 0;
  }
});

const toggleDropdown = () => {
  if (props.disabled) return;

  isOpen.value = !isOpen.value;
  hoveredIndex.value = -1;
};

const closeDropdown = () => {
  isOpen.value = false;
  hoveredIndex.value = -1;

  if (!props.modelValue?.trim()) {
    inputValue.value = '';

    return;
  }

  if (selectedLabel.value.toLowerCase() !== inputValue.value.toLowerCase()) {
    inputValue.value = '';
    emit('update:modelValue', '');
  }
};

const selectOption = (option: ComboboxOption) => {
  inputValue.value = option.label;

  emit('update:modelValue', option.value);

  nextTick(() => {
    closeDropdown();
  });
};

function handleKeydown(e: KeyboardEvent) {
  if (!isOpen.value) toggleDropdown();

  const list = filteredOptions.value;
  switch (e.key) {
    case 'ArrowDown':
      hoveredIndex.value = (hoveredIndex.value + 1) % list.length;
      break;
    case 'ArrowUp':
      hoveredIndex.value = hoveredIndex.value > 0 ? hoveredIndex.value - 1 : list.length - 1;
      break;
    case 'Enter':
      if (hoveredIndex.value >= 0 && list[hoveredIndex.value]) {
        selectOption(list[hoveredIndex.value]);
      }
      break;
    case 'Escape':
      if (isOpen.value) {
        closeDropdown();
      }
      break;
  }
}

onClickOutside(dropdownRef, closeDropdown);
</script>

<style lang="scss" scoped>
.dropdown-cnt {
  width: 100%;
  position: relative;
  padding-bottom: 10px;
}

.dropdown-list-cnt {
  width: 100%;
  max-height: 30rem;
  z-index: 99;
  padding: 0px;
  overflow: none;
  position: absolute;
  overflow-y: scroll;
  color: var(--primary-dark-color);
  background-color: var(--shade-1);
  box-shadow: 0px 5px 5px 0px rgba(0, 0, 0, 0.1);
}

.dropdown-list {
  margin: 0;
  padding: 0;
  cursor: pointer;
  text-align: left;
  list-style-type: none;

  .dropdown-item {
    color: var(--primary-dark-color);
    border: none;
    cursor: pointer;
    padding: 14px;
    width: 100%;
    font-size: medium;
    text-align: left;
    background-color: transparent;

    &.hover {
      background-color: var(--shade-2);
    }

    &.active {
      background-color: var(--shade-3);
    }
  }
}

.dropdown-input-cnt {
  position: relative;

  .combobox-input {
    width: 100%;
  }

  .combobox-icon {
    position: absolute;
    right: 1.8rem;
    top: 42%;
    transform: scale(1.5);
  }
}
</style>
